import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { EventsNavBadge } from './EventsNavBadge';

interface MobileMenuButtonProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

export function MobileMenuButton({ isMobileMenuOpen, setIsMobileMenuOpen }: MobileMenuButtonProps) {
  const { t } = useTranslation();

  return (
    <motion.button
      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      className={cn(
        "relative p-3 rounded-2xl transition-all duration-300 overflow-hidden",
        isMobileMenuOpen
          ? "bg-[#1E40AF] text-white shadow-lg shadow-blue-500/30"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      )}
      aria-label={t('common.toggleMenu')}
      whileTap={{ scale: 0.92 }}
    >
      <AnimatePresence mode="wait">
        {isMobileMenuOpen ? (
          <motion.div
            key="close"
            initial={{ rotate: -180, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 180, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <X className="w-5 h-5" />
          </motion.div>
        ) : (
          <motion.div
            key="menu"
            initial={{ rotate: 180, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -180, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Menu className="w-5 h-5" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

interface MobileNavProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  navLinks: { href: string; label: string }[];
  isActive: (path: string) => boolean;
}

export function MobileNav({ isMobileMenuOpen, setIsMobileMenuOpen, navLinks, isActive }: MobileNavProps) {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0, y: -20 }}
          animate={{ opacity: 1, height: 'auto', y: 0 }}
          exit={{ opacity: 0, height: 0, y: -20 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="lg:hidden overflow-hidden"
        >
          <div className="py-5 flex flex-col gap-2 bg-gradient-to-b from-white/80 to-slate-50/80 backdrop-blur-xl rounded-3xl mt-4 p-4 border border-slate-200/50 shadow-xl shadow-slate-900/5">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0, x: -30, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{
                  delay: index * 0.06,
                  type: 'spring',
                  stiffness: 300,
                  damping: 25
                }}
              >
                <Link
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'relative flex items-center gap-3 px-5 py-4 rounded-2xl text-sm font-bold transition-all duration-300 group overflow-hidden',
                    isActive(link.href)
                      ? 'text-white shadow-lg shadow-blue-500/25'
                      : 'text-slate-700 hover:text-[#1E40AF] hover:bg-white/80'
                  )}
                >
                  {isActive(link.href) && (
                    <>
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-[#1E40AF] via-[#2563eb] to-[#3b82f6]"
                        layoutId="activeMobile"
                        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                      />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-transparent"
                        animate={{
                          scale: [1, 1.05, 1],
                          opacity: [0.5, 0.8, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    </>
                  )}
                  <motion.span
                    className={cn(
                      'w-2 h-2 rounded-full transition-all relative z-10',
                      isActive(link.href)
                        ? 'bg-white shadow-lg shadow-white/50'
                        : 'bg-slate-300 group-hover:bg-[#1E40AF] group-hover:scale-125'
                    )}
                    animate={isActive(link.href) ? {
                      scale: [1, 1.3, 1],
                    } : {}}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                  <span className="relative z-10 flex items-center gap-2">
                    {link.label}
                    {link.href === '/events' && (
                      <EventsNavBadge active={isActive(link.href)} />
                    )}
                  </span>
                  {isActive(link.href) && (
                    <motion.div
                      className="ml-auto relative z-10"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                    </motion.div>
                  )}
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, type: 'spring', stiffness: 300, damping: 25 }}
              className="flex flex-col gap-3 pt-5 mt-3 border-t border-slate-200/70"
            >
              <Link
                to="/get-involved"
                onClick={() => setIsMobileMenuOpen(false)}
                className="relative flex items-center justify-center py-4 px-5 rounded-2xl font-bold border-2 border-[#1E40AF] text-[#1E40AF] hover:text-white transition-all duration-500 overflow-hidden group"
              >
                <div className="absolute inset-0 translate-y-[85%] group-hover:translate-y-0 transition-transform duration-700 ease-out">
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1E40AF] via-[#2563eb] to-[#3b82f6]" />
                  <svg
                    className="absolute -top-2 h-4 animate-wave w-[150%] left-[-25%]"
                    viewBox="0 0 120 16"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0,8 C20,3 40,13 60,8 C80,3 100,13 120,8 L120,16 L0,16 Z"
                      fill="#3b82f6"
                    />
                  </svg>
                </div>
                <span className="relative z-10">{t('nav.getInvolved')}</span>
              </Link>
              <Link
                to="/donate"
                onClick={() => setIsMobileMenuOpen(false)}
                className="relative flex items-center justify-center gap-2 py-4 px-5 rounded-2xl font-bold bg-gradient-to-r from-[#FBBF24] via-[#f59e0b] to-[#FBBF24] text-white shadow-md shadow-amber-500/20 overflow-hidden group"
                style={{
                  backgroundSize: '200% 100%',
                  backgroundPosition: '0% 0%',
                }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-[#f59e0b] to-[#FBBF24] opacity-30"
                  animate={{
                    x: ['-100%', '100%'],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                <Heart className="w-4 h-4 relative z-10 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
                <span className="relative z-10">{t('nav.donate')}</span>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
